import { useEffect, useState, useMemo } from 'react';
import { fetchSeoSettings } from '../api/seoApi';
import settingsApi from '../api/settingsApi';
import { SETTING_KEYS, API_BASE_URL } from '../constants';

const MetaTagsUpdater = ({pageKey, dynamicTitle = '', dynamicDescription = '', dynamicImage = '', onComplete,}) => {
  const [seoData, setSeoData] = useState(null);
  const [siteName, setSiteName] = useState('Rishabh Gupta');
  const [faviconUrl, setFaviconUrl] = useState(null);

  const skipSeoApiCallPageKeys = useMemo(
      () => [
        'BlogDetail',
        'ProjectDetail',
        'ServiceDetail',
        'NotFound',
      ],
      []
  );

  useEffect(() => {
    const getMetaData = async () => {
      try {
        // Fetch site name
        const siteNameResponse = await settingsApi.getSettingByKey(
            SETTING_KEYS.SITE_NAME
        );

        if (
            siteNameResponse.success &&
            siteNameResponse.data &&
            siteNameResponse.data.value
        ) {
          setSiteName(siteNameResponse.data.value);
        } else {
          setSiteName('Rishabh Gupta');
        }

        // Fetch favicon
        const faviconResponse = await settingsApi.getSettingByKey(
            SETTING_KEYS.FAVICON
        );

        if (
            faviconResponse.success &&
            faviconResponse.data &&
            faviconResponse.data.value
        ) {
          setFaviconUrl(faviconResponse.data.value);
        } else {
          setFaviconUrl(null);
        }

        // Fetch SEO settings
        if (pageKey) {
          if (skipSeoApiCallPageKeys.includes(pageKey)) {
            setSeoData(null);
          } else {
            try {
              const seoResponse = await fetchSeoSettings(pageKey);

              if (seoResponse) {
                setSeoData(seoResponse);
              } else {
                setSeoData(null);
              }
            } catch (seoError) {
              setSeoData(null);
            }
          }
        } else {
          setSeoData(null);
        }
      } catch (error) {
        setSeoData(null);
      }
    };

    getMetaData();
  }, [pageKey, skipSeoApiCallPageKeys]);

  useEffect(() => {
    const updateMetaTag = (
        name,
        content,
        property = false
    ) => {
      let tag = document.querySelector(
          `meta[${property ? 'property' : 'name'}="${name}"]`
      );

      if (!tag) {
        tag = document.createElement('meta');

        tag.setAttribute(
            property ? 'property' : 'name',
            name
        );

        document.head.appendChild(tag);
      }

      tag.setAttribute('content', content || '');
    };

    const updateLinkTag = (
        rel,
        href,
        type = null
    ) => {
      let tag = document.querySelector(
          `link[rel="${rel}"]`
      );

      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }

      tag.setAttribute('href', href);

      if (type) {
        tag.setAttribute('type', type);
      } else {
        tag.removeAttribute('type');
      }
    };

    // Determine final meta values
    const finalMetaTitle =
        dynamicTitle ||
        (seoData ? seoData.meta_title : '');

    const finalMetaDescription =
        dynamicDescription ||
        (seoData ? seoData.meta_description : '');

    const finalMetaKeywords =
        seoData ? seoData.meta_keyword : '';

    const finalCanonicalLink =
        seoData ? seoData.canonical_link : '';

    // Robots
    let robotsContent = 'index, follow';

    if (pageKey === 'NotFound') {
      robotsContent = 'noindex, nofollow';
    } else if (seoData && seoData.meta_robots) {
      robotsContent = seoData.meta_robots;
    }

    const title = finalMetaTitle
        ? `${finalMetaTitle} | ${siteName}`
        : siteName;

    const description = finalMetaDescription;
    const keywords = finalMetaKeywords;
    const canonical = finalCanonicalLink;
    const robots = robotsContent;

    let absoluteImageUrl = '';

    if (dynamicImage) {
      if (dynamicImage.startsWith('/storage/')) {
        absoluteImageUrl =
            `${API_BASE_URL}${dynamicImage}`;
      } else if (
          dynamicImage.startsWith('http://') ||
          dynamicImage.startsWith('https://')
      ) {
        absoluteImageUrl = dynamicImage;
      }
    }

    // Title
    document.title = title;

    // Basic meta tags
    updateMetaTag(
        'description',
        description
    );

    updateMetaTag(
        'keywords',
        keywords
    );

    // Canonical
    updateLinkTag(
        'canonical',
        canonical
            ? canonical
            : window.location.href
    );

    // Robots
    updateMetaTag(
        'robots',
        robots
    );

    // Open Graph
    updateMetaTag(
        'og:title',
        title,
        true
    );

    updateMetaTag(
        'og:description',
        description,
        true
    );

    updateMetaTag(
        'og:type',
        'website',
        true
    );

    updateMetaTag(
        'og:url',
        window.location.href,
        true
    );

    if (absoluteImageUrl) {
      updateMetaTag(
          'og:image',
          absoluteImageUrl,
          true
      );
    } else {
      document
          .querySelector(
              'meta[property="og:image"]'
          )
          ?.remove();
    }

    // Twitter
    updateMetaTag(
        'twitter:card',
        absoluteImageUrl
            ? 'summary_large_image'
            : 'summary'
    );

    updateMetaTag(
        'twitter:title',
        title
    );

    updateMetaTag(
        'twitter:description',
        description
    );

    if (absoluteImageUrl) {
      updateMetaTag(
          'twitter:image',
          absoluteImageUrl
      );
    } else {
      document
          .querySelector(
              'meta[name="twitter:image"]'
          )
          ?.remove();
    }

    // IMPORTANT:
    // Meta tags are now updated in the DOM.
    onComplete?.();

  }, [
    seoData,
    siteName,
    dynamicTitle,
    dynamicDescription,
    dynamicImage,
    pageKey,
    onComplete,
  ]);

  useEffect(() => {
    const updateLinkTag = (
        rel,
        href,
        type = null
    ) => {
      let tag = document.querySelector(
          `link[rel="${rel}"]`
      );

      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }

      tag.setAttribute('href', href);

      if (type) {
        tag.setAttribute('type', type);
      } else {
        tag.removeAttribute('type');
      }
    };

    let faviconMimeType = 'image/x-icon';

    if (faviconUrl) {
      if (faviconUrl.endsWith('.png')) {
        faviconMimeType = 'image/png';
      } else if (faviconUrl.endsWith('.ico')) {
        faviconMimeType = 'image/x-icon';
      } else if (faviconUrl.endsWith('.svg')) {
        faviconMimeType = 'image/svg+xml';
      }
    }

    document
        .querySelectorAll(
            'link[rel="icon"], link[rel="shortcut icon"]'
        )
        .forEach(link => link.remove());

    if (faviconUrl) {
      updateLinkTag(
          'icon',
          faviconUrl,
          faviconMimeType
      );

      updateLinkTag(
          'shortcut icon',
          faviconUrl,
          faviconMimeType
      );
    } else {
      updateLinkTag(
          'icon',
          '/favicon.ico',
          'image/x-icon'
      );

      updateLinkTag(
          'shortcut icon',
          '/favicon.ico',
          'image/x-icon'
      );
    }
  }, [faviconUrl]);

  return null;
};

export default MetaTagsUpdater;